/**
 * Corbeille et archives.
 *
 * Rien n'est supprimé sans une action explicite : une démarche archivée reste
 * consultable, une démarche en corbeille est restaurable, et la suppression
 * définitive est une opération distincte, refusée pour les obligations issues
 * de la bibliothèque administrative.
 */

import { useState } from "react";
import { Link } from "react-router-dom";

import { tasksApi } from "../api/endpoints";
import { useToast } from "../components/ToastContext";
import {
  Alert,
  Button,
  Card,
  Chip,
  EmptyState,
  ErrorAlert,
  LoadingBlock,
  PageHeader,
  StatusBadge,
} from "../components/ui";
import { useDocumentTitle, useMutation, useResource } from "../hooks/useApi";
import { formatDate, pluralize } from "../lib/format";

export function TrashPage() {
  useDocumentTitle("Corbeille et archives");
  const toast = useToast();
  const [view, setView] = useState<"trashed" | "archived">("trashed");

  const tasks = useResource(
    () =>
      tasksApi.list({
        only_trashed: view === "trashed" || undefined,
        only_archived: view === "archived" || undefined,
        limit: 100,
        sort: "created",
        direction: "desc",
      }),
    [view],
  );

  const restore = useMutation((id: string) => tasksApi.restore(id), {
    onSuccess: async () => {
      await tasks.reload();
      toast.success("Démarche restaurée");
    },
    onError: (error) => toast.error("Restauration impossible", error.message),
  });

  const unarchive = useMutation((id: string) => tasksApi.unarchive(id), {
    onSuccess: async () => {
      await tasks.reload();
      toast.success("Démarche sortie des archives");
    },
  });

  const removeForever = useMutation(
    (id: string) => tasksApi.deletePermanently(id),
    {
      onSuccess: async () => {
        await tasks.reload();
        toast.success("Démarche définitivement supprimée");
      },
      onError: (error) => toast.error("Suppression refusée", error.message),
    },
  );

  const items = tasks.data?.items ?? [];

  return (
    <>
      <PageHeader
        title="Corbeille et archives"
        subtitle="Les démarches mises de côté sont conservées : rien n'est perdu sans votre accord explicite."
      />

      <div className="chips" role="group" aria-label="Vue">
        <Chip
          label="Corbeille"
          pressed={view === "trashed"}
          onToggle={() => setView("trashed")}
        />
        <Chip
          label="Archives"
          pressed={view === "archived"}
          onToggle={() => setView("archived")}
        />
      </div>

      <Alert tone="info" title={view === "trashed" ? "Corbeille" : "Archives"}>
        {view === "trashed"
          ? "Ces démarches n'apparaissent plus dans votre liste courante, mais leurs données et leur historique sont intacts. Vous pouvez les restaurer à tout moment."
          : "Ces démarches ont été archivées, soit par vous, soit automatiquement parce qu'elles ne correspondent plus à votre profil entreprise. Elles restent consultables."}
      </Alert>

      <ErrorAlert error={tasks.error} title="Liste indisponible" />

      <Card
        flush
        title={
          tasks.data
            ? `${pluralize(tasks.data.total, "démarche")} ${
                view === "trashed" ? "dans la corbeille" : "archivée" + (tasks.data.total > 1 ? "s" : "")
              }`
            : undefined
        }
      >
        {tasks.loading ? (
          <LoadingBlock />
        ) : items.length === 0 ? (
          <EmptyState
            title={
              view === "trashed" ? "La corbeille est vide" : "Aucune démarche archivée"
            }
          />
        ) : (
          <div className="table-wrap">
            <table className="data">
              <caption className="visually-hidden">
                {view === "trashed" ? "Démarches en corbeille" : "Démarches archivées"}
              </caption>
              <thead>
                <tr>
                  <th scope="col">Démarche</th>
                  <th scope="col">Statut</th>
                  <th scope="col">Origine</th>
                  <th scope="col">Échéance</th>
                  <th scope="col">Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map((task) => (
                  <tr key={task.id}>
                    <td>
                      <Link to={`/demarches/${task.id}`}>{task.title}</Link>
                      {task.period_label && (
                        <div className="inline-note">{task.period_label}</div>
                      )}
                    </td>
                    <td>
                      <StatusBadge status={task.status} label={task.status_label} />
                    </td>
                    <td>
                      {task.source === "manual" ? (
                        <span className="badge">Personnelle</span>
                      ) : (
                        <span className="badge badge--info">Bibliothèque</span>
                      )}
                    </td>
                    <td className="nowrap">{formatDate(task.due_on)}</td>
                    <td>
                      <div className="btn-group">
                        <Button
                          size="sm"
                          variant="quiet"
                          onClick={() =>
                            void (view === "trashed"
                              ? restore.run(task.id)
                              : unarchive.run(task.id))
                          }
                        >
                          Restaurer
                        </Button>
                        {view === "trashed" && task.source === "manual" && (
                          <Button
                            size="sm"
                            variant="quiet"
                            onClick={() => void removeForever.run(task.id)}
                          >
                            Supprimer définitivement
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {view === "trashed" && (
        <Card title="Pourquoi certaines démarches ne peuvent pas être supprimées ?">
          <p className="text-muted mb-0">
            Une obligation issue de la bibliothèque administrative ne peut pas être
            supprimée définitivement : elle serait régénérée au prochain calcul, et
            son historique — notamment la trace d'une validation — serait perdu.
            Vous pouvez l'archiver. Une obligation déjà validée reste par ailleurs
            dans votre historique : c'est la preuve de ce que vous avez fait.
          </p>
        </Card>
      )}
    </>
  );
}
